import { useEffect, useRef, useState } from "react";
import { CaretDown, Check, UsersThree, X } from "@phosphor-icons/react";
import type { Id } from "../../convex/_generated/dataModel";
import { useDirtySyncedState } from "../lib/useDirtySyncedState.ts";
import { ConfirmDialog } from "./ConfirmDialog.tsx";
import {
  canBringEveryoneHere,
  makeLiveBrowserContinuityCopy,
} from "./sessionHandoffControl.ts";

export interface SessionListItemLite {
  id: Id<"sessions">;
  title: string;
  joinCode: string | null;
  kind: "test" | "rehearsal" | "live";
  status: "draft" | "active" | "closed" | "archived";
  startedAt: number;
}

export interface SessionControlProps {
  selectedId: Id<"sessions">;
  liveId: Id<"sessions"> | null;
  title: string;
  sessions: SessionListItemLite[] | undefined;
  optionLabel: (s: SessionListItemLite) => string;
  onRename: (newTitle: string) => Promise<void> | void;
  onSelect: (id: Id<"sessions">) => void;
  onMakeLive: (id: Id<"sessions">) => Promise<void> | void;
  onEndLive: (id: Id<"sessions">) => Promise<void> | void;
  onBringEveryoneHere: () => Promise<void> | void;
  onNew: () => void;
}

export function SessionControl({
  selectedId,
  liveId,
  title,
  sessions,
  optionLabel,
  onRename,
  onSelect,
  onMakeLive,
  onEndLive,
  onBringEveryoneHere,
  onNew,
}: SessionControlProps) {
  const {
    draft,
    setDraft,
    dirty,
    resetToBaseline,
  } = useDirtySyncedState({
    value: title,
    resetKey: selectedId,
    isEqual: sessionTitleEqual,
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const [liveAction, setLiveAction] = useState<"activate" | "end" | null>(null);
  const [liveBusy, setLiveBusy] = useState(false);
  const [liveError, setLiveError] = useState<string | null>(null);
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [handoffBusy, setHandoffBusy] = useState(false);
  const [handoffError, setHandoffError] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const handoffBusyRef = useRef(false);

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    window.addEventListener("mousedown", onDoc);
    return () => window.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const normalized = draft.trim() || "New session";

  function confirm() {
    if (!dirty) return;
    void onRename(normalized);
  }
  function cancel() {
    resetToBaseline();
  }

  const selectedSession = sessions?.find((session) => session.id === selectedId) ?? null;
  const selectedIsLive = selectedId === liveId;
  const selectedIsArchived = selectedSession?.status === "archived";
  const handoffAvailable = canBringEveryoneHere(
    selectedId,
    liveId,
    selectedSession?.status,
  );

  useEffect(() => {
    if (handoffAvailable) return;
    setHandoffOpen(false);
    setHandoffError(null);
  }, [handoffAvailable]);

  async function confirmLiveAction() {
    if (!liveAction || liveBusy) return;
    setLiveBusy(true);
    setLiveError(null);
    try {
      if (liveAction === "end") await onEndLive(selectedId);
      else await onMakeLive(selectedId);
      setLiveAction(null);
    } catch (error) {
      setLiveError(error instanceof Error ? error.message : "Could not change the live session.");
    } finally {
      setLiveBusy(false);
    }
  }

  async function confirmHandoff() {
    if (!handoffAvailable || handoffBusyRef.current) return;
    handoffBusyRef.current = true;
    setHandoffBusy(true);
    setHandoffError(null);
    try {
      await onBringEveryoneHere();
      setHandoffOpen(false);
    } catch {
      setHandoffError("Could not send the switch command. Please try again.");
    } finally {
      handoffBusyRef.current = false;
      setHandoffBusy(false);
    }
  }

  return (
    <div className="session-control" data-testid="session-control" ref={wrapRef}>
      <div className="session-name-field">
        <input
          name="sessionName"
          aria-label="Session name"
          className="session-name-input"
          data-testid="session-name-input"
          value={draft}
          maxLength={120}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); confirm(); }
            if (e.key === "Escape") cancel();
          }}
        />
        {dirty && (
          <div className="session-name-actions">
            <button type="button" className="icon-btn confirm" data-testid="session-name-confirm" title="Save name" onClick={confirm}>
              <Check weight="bold" />
            </button>
            <button type="button" className="icon-btn cancel" data-testid="session-name-cancel" title="Cancel" onClick={cancel}>
              <X weight="bold" />
            </button>
          </div>
        )}
      </div>
      <button
        type="button"
        className="icon-btn session-switcher-toggle"
        data-testid="session-switcher-toggle"
        title="Switch viewed session"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((v) => !v)}
      >
        <CaretDown weight="bold" />
      </button>
      <button
        type="button"
        className={`pill session-live-toggle ${selectedIsLive ? "on" : "off"}`}
        data-testid="session-live-toggle"
        disabled={selectedIsArchived}
        aria-pressed={selectedIsLive}
        onClick={() => {
          setLiveError(null);
          setLiveAction(selectedIsLive ? "end" : "activate");
        }}
      >
        {selectedIsArchived ? "Archived" : selectedIsLive ? "LIVE" : "Make live"}
      </button>
      {handoffAvailable && (
        <button
          type="button"
          className="btn ghost sm session-handoff-trigger"
          data-testid="session-handoff-trigger"
          aria-label="Bring everyone here"
          title="Move open session views to this live session"
          onClick={() => {
            setHandoffError(null);
            setHandoffOpen(true);
          }}
        >
          <UsersThree weight="bold" aria-hidden="true" />
          <span className="session-handoff-label">Bring everyone here</span>
        </button>
      )}
      {menuOpen && (
        <div className="session-menu" data-testid="session-menu" role="menu">
          <button
            type="button"
            className="session-menu-new"
            data-testid="session-menu-new"
            onClick={() => { setMenuOpen(false); onNew(); }}
          >
            + New session
          </button>
          <div className="session-menu-list">
            {(sessions ?? []).map((s) => (
              <button
                type="button"
                key={s.id}
                data-testid="session-menu-item"
                className={`session-menu-item ${s.id === selectedId ? "is-selected" : ""}`}
                onClick={() => { setMenuOpen(false); onSelect(s.id); }}
              >
                <span className="session-menu-item-label">{optionLabel(s)}</span>
                {s.id === liveId && <span className="session-menu-live-badge">LIVE</span>}
              </button>
            ))}
          </div>
        </div>
      )}
      {liveAction && (
        <ConfirmDialog
          title={liveAction === "end" ? "End live session?" : "Make this session live?"}
          confirmLabel={liveAction === "end" ? "End live" : "Make live"}
          busyLabel={liveAction === "end" ? "Ending…" : "Switching…"}
          busy={liveBusy}
          destructive={liveAction === "end"}
          error={liveError}
          onCancel={() => {
            setLiveAction(null);
            setLiveError(null);
          }}
          onConfirm={() => void confirmLiveAction()}
        >
          {liveAction === "end" ? (
            <p>
              End <strong>{selectedSession?.title ?? title}</strong> as the live session? Its final summary
              process will begin.
            </p>
          ) : (
            <p>
              Make <strong>{selectedSession?.title ?? title}</strong> live?
              {makeLiveBrowserContinuityCopy(Boolean(liveId && liveId !== selectedId))}
            </p>
          )}
        </ConfirmDialog>
      )}
      {handoffOpen && handoffAvailable && (
        <ConfirmDialog
          title="Bring everyone to this session?"
          confirmLabel="Bring everyone here"
          busyLabel="Sending…"
          busy={handoffBusy}
          error={handoffError}
          onCancel={() => {
            setHandoffOpen(false);
            setHandoffError(null);
          }}
          onConfirm={() => void confirmHandoff()}
        >
          <p>
            Move open participant, admin dashboard, moderator, and projector session views to{" "}
            <strong>{selectedSession?.title ?? title}</strong>?
          </p>
          <p>
            Session history and configuration tools will stay where they are. The command cannot
            report an exact number of browsers moved.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
}

function sessionTitleEqual(a: string, b: string): boolean {
  return normalizeTitle(a) === normalizeTitle(b);
}

function normalizeTitle(value: string): string {
  return value.trim() || "New session";
}
